<template>
  <div class="search-url">
    <sib-input
      @blur="checkCustomUrl"
      v-model="inputUrl"
      size="md"
      round="md"
      :placeholder="$props.placeholder"
      :required="true"
      :name="`properties[${title}]`"
      :readonly="loading"
      @keypress="isTypeUrl"
    >
    </sib-input>

    <div v-if="loading" class="search-loading lds-ring">
      <div></div>
      <div></div>
      <div></div>
      <div></div>
    </div>

    <div class="search-url__message" v-show="!isSuccess">
      {{ messageUrlExisted }}
    </div>

    <input
      v-if="attributeKey"
      type="hidden"
      :name="`properties[${attributeKey + title}]`"
      :value="inputUrl"
    />
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, watch } from 'vue'
import {
  CUSTOMILY_FILE_INPUT_WRAPPER_CLASS,
  CUSTOMILY_OPTION_SORT_ID_WRAPPER_SELECTOR,
  EVENT_CHECKING_URL,
  EVENT_CUSTOM_UPLOAD_IMAGE,
  INPUT_FILE_CUSTOMILY_SELECTOR,
  INPUT_TEXT_CUSTOMILY_SELECTOR,
  QUERY_STRING_NO_CACHE,
} from '@/utils'
import { useRoute } from 'vue-router'
import Provider from '@/provider'
import useCustomizationStore from '@/store/customization'

import { useProductCustomization } from './composables/ProductCustomization'
import EventBus from '@/services/eventbus'

const {
  convertImageLinkToFileList,
  changeFileImageCustomily,
  changeInputCustomily,
} = useProductCustomization()

const customizationStore = useCustomizationStore()
const route = useRoute()

const props = withDefaults(
  defineProps<{
    title?: string
    placeholder?: string
    attributeKey?: string
    sortId?: string | number
    color?: string
    textSortId: string | number
  }>(),
  {}
)

const inputUrl = ref('')
const recentlyInputUrl = ref('')
const isSuccess = ref(true)
const messageUrlExisted =
  'This url already exists, please enter a different url.'
const qrCodeUrl = ref('')
const loading = ref(false)

onMounted(() => {
  hiddenOptionWrapperBySortId(props.sortId)
  hiddenOptionWrapperBySortId(props.textSortId)
  watch(
    () => loading.value,
    (value) => {
      EventBus.trigger(EVENT_CUSTOM_UPLOAD_IMAGE, value)
    }
  )
})

async function checkCustomUrl() {
  if (!inputUrl.value) return
  if (inputUrl.value === recentlyInputUrl.value) return
  recentlyInputUrl.value = inputUrl.value
  EventBus.trigger(EVENT_CHECKING_URL, true)
  loading.value = true
  const provider = await Provider.getInstance()
  const response = await provider.checkUrlBlogGreetingCard({
    url: inputUrl.value,
  })
  if (response.status) {
    isSuccess.value = response.status
    await generateQrCodeByUrl(inputUrl.value)
  } else {
    isSuccess.value = false
  }
  loading.value = false
  EventBus.trigger(EVENT_CHECKING_URL, false)
}

async function generateQrCodeByUrl(url: string) {
  if (!url) return
  let imageQrlUrl = null

  imageQrlUrl = await customizationStore.generateQrCodeByUri({
    uri: buildBlogUrl(url, true),
    color: props.color || '#000000',
    isForceLoadNewData: !!route.query[QUERY_STRING_NO_CACHE],
  })

  if (typeof imageQrlUrl == 'string') qrCodeUrl.value = imageQrlUrl
  if (!imageQrlUrl || !props.sortId || !props.textSortId) return

  const inputTextCustomily = getInputCustomilyBySortId(props.textSortId, 'text')

  if (inputTextCustomily) {
    changeInputCustomily(buildBlogUrl(url, false), inputTextCustomily)
  }

  const inputCustomily = getInputCustomilyBySortId(props.sortId, 'image')

  if (inputCustomily) {
    const imageFileList = await convertImageLinkToFileList(
      qrCodeUrl.value,
      'qrCodeUrlBlog'
    )
    changeFileImageCustomily(imageFileList, inputCustomily)
  }
}

function getInputCustomilyBySortId(sortId: string | number, type: string) {
  const customilyOptionWrapper = document.querySelector(
    `.${CUSTOMILY_OPTION_SORT_ID_WRAPPER_SELECTOR.replace(
      'option_sort_id',
      sortId.toString()
    )}`
  )
  if (!customilyOptionWrapper) return

  if (type == 'image') {
    const customilyFileInputWrapper = customilyOptionWrapper.querySelector(
      `.${CUSTOMILY_FILE_INPUT_WRAPPER_CLASS}`
    )

    if (!customilyFileInputWrapper) return
    return customilyFileInputWrapper.querySelector(
      INPUT_FILE_CUSTOMILY_SELECTOR
    )
  }
  if (type == 'text') {
    return customilyOptionWrapper.querySelector(INPUT_TEXT_CUSTOMILY_SELECTOR)
  }
}

function hiddenOptionWrapperBySortId(sortId?: string | number) {
  if (!sortId) return
  const customilyOptionWrapper = document.querySelector(
    `.${CUSTOMILY_OPTION_SORT_ID_WRAPPER_SELECTOR.replace(
      'option_sort_id',
      sortId.toString()
    )}`
  )
  if (!customilyOptionWrapper) return
  customilyOptionWrapper.style.display = 'none'
}

function buildBlogUrl(handle: string, isHTTPs: boolean) {
  if (!handle) return ''
  return isHTTPs
    ? `${window.location.origin}/${handle}`
    : `${window.location.hostname}/${handle}`
}

function isTypeUrl(event: Event) {
  const pattern = /^[a-z0-9-]$/
  const res = event?.key?.match(pattern)
  if (!res) {
    event.preventDefault()
    return false
  }
}
</script>

<style lang="scss">
.search-url {
  position: relative;
  &__message {
    color: var(--color-secondary);
    font-size: 0.75rem;
    margin-top: 0.25rem;
    margin-left: 0.25rem;
  }

  .search-loading {
    position: absolute;
    top: 50%;
    right: 1rem;
    transform: translateY(-50%);
    --loading-size: 16px;
    --loading-line-width: 2px;
  }
}
</style>
